import { useState, useCallback, useRef, useEffect } from "react"
import { IAge } from "../../../../../../../application/models"
import { useRecortesExtracaoService } from "../../../../../../../use-cases/geo-veles"
import { useUIService } from "../../../../../../../use-cases/ui"
import { Grid, InputAdornment, TextField } from "@mui/material"
import { useAppSelector } from "../../../../../../hooks/store-hooks"

export const RecorteIdade = () => {
    const { editRecorteExtracaoForm } = useAppSelector((state) => state.recorteExtracao)
    // Use State
    const [selectedIdade, setSelectedIdade] = useState<IAge>({})
    const minFieldTouched = useRef(false)
    const maxFieldTouched = useRef(false)

    // Constantes de validação
    const MIN_AGE = 18
    const MAX_AGE = 120

    // Hooks
    const { setEditRecorteFormFiltros, removeEditRecorteFormFiltros } = useRecortesExtracaoService()
    const { setAlert } = useUIService()

    // Sincronização inicial com o estado global
    useEffect(() => {
        const idadeFromForm = editRecorteExtracaoForm?.fields?.Filtros?.idade;

        if (idadeFromForm &&
            (idadeFromForm.min !== undefined || idadeFromForm.max !== undefined)) {
            setSelectedIdade(idadeFromForm);

            if (idadeFromForm.min !== undefined) minFieldTouched.current = true;
            if (idadeFromForm.max !== undefined) maxFieldTouched.current = true;
        } else {
            setSelectedIdade({});
            minFieldTouched.current = false;
            maxFieldTouched.current = false;
        }
    }, [editRecorteExtracaoForm?.fields?.Filtros?.idade]);

    // Função para verificar se a idade é válida e completa (1-3 dígitos)
    const isValidCompleteAge = (age: number | undefined): boolean => {
        if (age === undefined) return false;
        if (isNaN(age)) return false;

        // Verifica se tem entre 1 e 3 dígitos
        const ageString = age.toString();
        return ageString.length >= 1 && ageString.length <= 3;
    }

    // Função para sincronizar com o estado global
    const syncWithGlobalState = useCallback((data: IAge) => {
        const hasValidMin = isValidCompleteAge(data.min);
        const hasValidMax = isValidCompleteAge(data.max);

        // Verifica se os campos foram tocados
        const minWasTouched = minFieldTouched.current;
        const maxWasTouched = maxFieldTouched.current;

        // MUDANÇA PRINCIPAL: Só valida e sincroniza se AMBOS os campos estiverem preenchidos
        const bothFieldsFilled = hasValidMin && hasValidMax;

        // Se ambos os campos não estão preenchidos, remove o filtro se algum foi tocado
        if (!bothFieldsFilled) {
            if (minWasTouched || maxWasTouched) {
                removeEditRecorteFormFiltros({
                    filtro: 'idade'
                });
            }
            return;
        }

        // A partir daqui, ambos os campos estão preenchidos e completos
        // Valida as regras de negócio
        if (data.min! < MIN_AGE) {
            setAlert({
                message: 'A idade mínima deve ser 18 anos',
                type: 'error'
            })
            return;
        }

        if (data.max! > MAX_AGE) {
            setAlert({
                message: 'A idade máxima deve ser 120 anos',
                type: 'error'
            })
            return;
        }

        if (data.max! < data.min!) {
            setAlert({
                message: 'A idade máxima deve ser maior que a idade mínima',
                type: 'error'
            })
            return;
        }

        // Sincroniza apenas se ambos os campos estão válidos
        setEditRecorteFormFiltros({
            filtro: 'idade',
            value: data
        });
    }, [setAlert, setEditRecorteFormFiltros, removeEditRecorteFormFiltros])

    // Effect para validar quando os campos mudarem
    useEffect(() => {
        // Só tenta sincronizar se algum campo foi tocado
        if (!minFieldTouched.current && !maxFieldTouched.current) {
            return;
        }

        const timer = setTimeout(() => {
            syncWithGlobalState(selectedIdade);
        }, 500);

        return () => clearTimeout(timer);
    }, [selectedIdade, syncWithGlobalState])

    // Função para lidar com a mudança de idade
    const handleAgeChange = useCallback((field: 'min' | 'max') =>
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const regexValue = e.target.value.replace(/\D/g, '')

            // Limita a 3 dígitos
            const limitedValue = regexValue.slice(0, 3);
            const newValue = limitedValue.length === 0 ? undefined : Number(limitedValue);

            // Marca o campo como tocado
            if (field === 'min') minFieldTouched.current = true
            if (field === 'max') maxFieldTouched.current = true

            setSelectedIdade(prev => ({
                ...prev,
                [field]: newValue
            }));
        }
        , [])

    return (
        <>
            <Grid item xs={5.5} my={2} px={2}>
                <TextField
                    value={selectedIdade.min ?? ''}
                    fullWidth
                    label="Idade mínima"
                    InputLabelProps={{
                        shrink: true
                    }}
                    InputProps={{
                        endAdornment: <InputAdornment position="end">anos</InputAdornment>
                    }}
                    onChange={handleAgeChange('min')}
                />
            </Grid>
            <Grid item xs={5.5} my={2} px={2}>
                <TextField
                    value={selectedIdade.max ?? ''}
                    fullWidth
                    label="Idade máxima"
                    InputLabelProps={{
                        shrink: true
                    }}
                    InputProps={{
                        endAdornment: <InputAdornment position="end">anos</InputAdornment>
                    }}
                    onChange={handleAgeChange('max')}
                />
            </Grid>
        </>
    )
}